import React, { useCallback } from 'react';
import type { PrimitiveType } from 'intl-messageformat';
import { dismissFlag as createDismissFlag, useFlagService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import type { CrossJoinIntegraton } from '../../common/types.tsx';
import { getDynamicMessages, type EntityId } from '../../services/dynamic-messaging/index.tsx';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';
import { messages } from '../../common/ui/request-access-prompt-flag/messages.tsx';

import {
	RequestAccessPromptFlag as RequestAccessPromptFlagCommon,
	type CJNMessageStrings,
} from './flag-common.tsx';

export const useRequestAccessPromptFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	return useCallback(
		({
			users,
			onOptout,
			onSubmit,
			snoozeFor30Days,
			snoozeFor7Days,
			integration,
			eventAttributes,
			messageParser,
			featureVisitedCount = 0,
		}: {
			users: Collaborators;
			onOptout: () => unknown;
			onSubmit: () => Promise<void>;
			snoozeFor30Days: () => unknown;
			snoozeFor7Days?: () => unknown;
			messageParser: (formatter: typeof formatMessage) => CJNMessageStrings;
			eventAttributes: AnalyticsAttributes;
			integration: CrossJoinIntegraton;
			sessionId?: string;
			featureVisitedCount?: number;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', integration, eventAttributes);
				dismissTheFlag();
				await onSubmit();
			};

			const dismissAndOptout = () => {
				dismissTheFlag();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag optedOut', integration, eventAttributes);
				onOptout();
			};

			const dismissAndSnooze = () => {
				dismissTheFlag();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', integration, {
					...eventAttributes,
					snoozeDays: 30,
				});
				snoozeFor30Days();
			};

			const dismiss = () => {
				const isFirstVisit = featureVisitedCount === 0;
				// Snooze for 7 days if dismissFor7Days is enabled and snoozeFor7Days function is provided
				if (isFirstVisit) {
					if (snoozeFor7Days) {
						dismissTheFlag();
						fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', integration, {
							...eventAttributes,
							snoozeDays: 7,
						});
						snoozeFor7Days();
					} else {
						dismissTheFlag();
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'flag dismissed',
							integration,
							eventAttributes,
						);
					}
				} else {
					dismissAndSnooze();
				}
			};

			fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', integration, eventAttributes);

			const cjnMessages = messageParser(formatMessage);
			const avatarOrientation =
				integration === 'crossJoinNudgesJiraToJpd' || integration === 'crossJoinNudgesJiraToLoom'
					? 'horizontal'
					: 'vertical';

			showFlag({
				id,
				render: (props) => (
					<RequestAccessPromptFlagCommon
						{...props}
						id={id}
						users={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onOptoutAndDismiss={dismissAndOptout}
						onSubmit={submit}
						avatarOrientation={avatarOrientation}
						messages={cjnMessages}
						featureVisitedCount={featureVisitedCount}
					/>
				),
			});
		},
		[formatMessage, createAnalyticsEvent, dismissFlag, showFlag],
	);
};

export function secondsElapsedSince(timestampInMs: number): string {
	const currentTime = Date.now();
	return ((currentTime - timestampInMs) / 1000).toFixed(3);
}

type CommonAnalyticsArgs = {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
	startTimestamp: number;
	snoozeDays?: number;
};
export const getAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	featureVisitedCount,
	startTimestamp,
	snoozeDays,
}: CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		sessionId,
		featureVisitedCount,
		eligibilityLatencySeconds: secondsElapsedSince(startTimestamp),
		snoozeDays,
	};
};

export const getContextualBanditAnalyticsAttributes = ({
	entityId,
	isContextualBandit,
	...args
}: {
	entityId: EntityId;
	isContextualBandit: boolean;
} & CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		...getAnalyticsAttributes(args),
		isContextualBandit,
		entityId,
	};
};

export const getLLMAnalyticsAttributes = ({
	firstName,
	companyName,
	generatedTitle,
	generatedBody,
	...rest
}: {
	firstName: string;
	companyName: string;
	generatedTitle: string;
	generatedBody: string;
} & CommonAnalyticsArgs): AnalyticsAttributes => {
	function normalize(content: string): string {
		return content.replaceAll(/[\W\d_]/g, '').toLowerCase();
	}

	function hasMatchingValue(value: string): boolean {
		const r = new RegExp(normalize(value));
		return Boolean(
			value && (r.test(normalize(generatedTitle)) || r.test(normalize(generatedBody))),
		);
	}

	return {
		...getAnalyticsAttributes(rest),
		contentHasFirstName: hasMatchingValue(firstName),
		contentHasCompanyName: hasMatchingValue(companyName),
		isLLMv2: true,
	};
};

export const getContextualBanditCJNMessageParser = ({
	isContextualBandit,
	entityId,
	featureVisitedCount,
}: {
	isContextualBandit: boolean;
	entityId: EntityId;
	featureVisitedCount: number;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		const dynamicMessages = getDynamicMessages(isContextualBandit, entityId) || messages;
		const infoFlagTitle = isContextualBandit
			? formatMessage(messages.title)
			: formatMessage(messages.urgencyTitle, { viewCount: featureVisitedCount });

		return {
			title: infoFlagTitle,
			description: formatMessage(dynamicMessages.description),
			joinButton: formatMessage(dynamicMessages.joinButton),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};

export const getJPDCJNMessageStrings = (
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
): CJNMessageStrings => {
	return {
		title: formatMessage(messages.titleJpdNudge),
		description: formatMessage(messages.descriptionJpdNudge),
		joinButton: formatMessage(messages.getStartedButton),
		remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
		notInterestedButton: formatMessage(messages.doNotShowAgainButton),
	};
};

export const getLoomCJNMessageStrings = ({
	collaborators,
}: {
	collaborators: Collaborators;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		const getDescription = () => {
			if (collaborators.length === 0) {
				return formatMessage(messages.descriptionLoomNudge);
			}

			const [first, second] = collaborators;

			let description;

			if (collaborators.length === 1) {
				description = formatMessage(messages.descriptionLoomNudgeWithOneCollaborator, {
					collaboratorOne: first.name,
				});
			} else if (collaborators.length === 2) {
				description = formatMessage(messages.descriptionLoomNudgeWithTwoCollaborators, {
					collaboratorOne: first.name,
					collaboratorTwo: second.name,
				});
			} else {
				description = formatMessage(messages.descriptionLoomNudgeWithMoreThanTwoCollaborators, {
					collaboratorOne: first.name,
					collaboratorCount: collaborators.length - 1,
				});
			}

			return description;
		};

		return {
			title: formatMessage(messages.titleLoomNudge),
			description: getDescription(),
			joinButton: formatMessage(messages.getStartedButton),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};

export const getLLMCJNMessageParser = ({
	title,
	description,
}: {
	title: string;
	description: string;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		return {
			title,
			description,
			joinButton: formatMessage(messages.joinButtonLlmGeneratedV2),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButtonLlmGeneratedV2),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};
